import { PayLinkField } from "./blocks/Pay";
import React from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Plus, Trash2, Upload, AlignLeft, AlignCenter, AlignRight, X } from "lucide-react";
import { uid, SOCIAL_PLATFORMS, TEXT_FONTS, TEXT_ANIMS, BUTTON_ANIMS, DIVIDER_STYLES, BUTTON_SHAPES } from "../mock";
import { apiUploadFile } from "../api";
import { BUTTON_ICONS } from "./PortfolioRenderer";

const PdfUploadField = ({ value, onChange }) => {
  const [pct, setPct] = React.useState(null);
  const [name, setName] = React.useState("");
  const pick = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) return alert("Selecciona un archivo PDF");
    setName(file.name);
    setPct(0);
    try {
      const { url } = await apiUploadFile(file, setPct);
      onChange(url);
    } catch (e) {
      alert("No se pudo subir el PDF. Intenta de nuevo.");
    } finally {
      setPct(null);
    }
  };
  return (
    <Field label="Archivo PDF">
      <label className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-slate-200 px-3 py-3 transition hover:border-blue-400" data-testid="pdf-upload-field">
        <Upload className="h-5 w-5 shrink-0 text-blue-600" />
        <span className="min-w-0 flex-1 truncate text-sm text-slate-600">
          {pct !== null ? `Subiendo ${name}… ${pct}%` : value ? "PDF cargado · toca para cambiarlo" : "Subir PDF (catálogo, menú, ficha…)"}
        </span>
        <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="pdf-upload-input" />
      </label>
    </Field>
  );
};

const TitleSize = ({ block, set, defaultSize = 14, field = "titleSize", label = "Tamaño del título" }) => (
  <div className="space-y-1.5 rounded-xl bg-slate-50 p-3">
    <div className="flex items-center justify-between">
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      <span className="text-xs font-semibold text-blue-600">
        {block[field] || defaultSize}px
      </span>
    </div>
    <input
      type="range"
      min="12"
      max="48"
      step="1"
      value={block[field] || defaultSize}
      onChange={(e) => set({ [field]: Number(e.target.value) })}
      className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
    />
  </div>
);

const SubtitleSize = ({ block, set }) => (
  <TitleSize block={block} set={set} field="subtitleSize" label="Tamaño del subtítulo" />
);

const TextStyleControls = ({ block, set, defaultSize }) => (
  <div className="space-y-3 rounded-xl bg-slate-50 p-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
      Estilo del texto
    </p>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-500">Tipografía</Label>
      <Select value={block.fontFamily || ""} onValueChange={(v) => set({ fontFamily: v })}>
        <SelectTrigger>
          <SelectValue placeholder="Predeterminada" />
        </SelectTrigger>
        <SelectContent>
          {TEXT_FONTS.map((f) => (
            <SelectItem key={f.label} value={f.value}>
              <span style={{ fontFamily: f.value }}>{f.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-slate-500">Tamaño</Label>
          <span className="text-xs font-semibold text-blue-600">
            {block.fontSize || defaultSize}px
          </span>
        </div>
        <input
          type="range"
          min="10"
          max="60"
          step="1"
          value={block.fontSize || defaultSize}
          onChange={(e) => set({ fontSize: Number(e.target.value) })}
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-slate-500">Color</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.color || "#ffffff"}
            onChange={(e) => set({ color: e.target.value })}
            className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white"
          />
          <button
            type="button"
            onClick={() => set({ color: "" })}
            className="text-xs text-slate-400 underline"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-500">Animación</Label>
      <Select value={block.anim || "none"} onValueChange={(v) => set({ anim: v })}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TEXT_ANIMS.map((a) => (
            <SelectItem key={a.value} value={a.value}>
              {a.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Alineación del texto */}
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-500">Alineación</Label>
      <div className="grid grid-cols-3 gap-2">
        {[
          { key: "left", Icon: AlignLeft, label: "Izq." },
          { key: "center", Icon: AlignCenter, label: "Centro" },
          { key: "right", Icon: AlignRight, label: "Der." },
        ].map((opt) => {
          const active = (block.align || "center") === opt.key;
          const I = opt.Icon;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => set({ align: opt.key })}
              className={`flex items-center justify-center gap-1 rounded-lg border-2 py-2 text-xs font-semibold transition ${
                active
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-slate-200 text-slate-600 hover:border-blue-300"
              }`}
              data-testid={`text-align-${opt.key}`}
            >
              <I className="h-4 w-4" /> {opt.label}
            </button>
          );
        })}
      </div>
    </div>

    {/* Espaciado entre letras */}
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium text-slate-500">Separación entre letras</Label>
        <span className="text-xs font-semibold text-blue-600">
          {(block.letterSpacing ?? 0).toFixed(2)}em
        </span>
      </div>
      <input
        type="range"
        min="-0.05"
        max="0.6"
        step="0.01"
        value={block.letterSpacing ?? 0}
        onChange={(e) => set({ letterSpacing: Number(e.target.value) })}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
      />
    </div>

    {/* Separación arriba/abajo y a los lados */}
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-slate-500">Arriba/Abajo</Label>
          <span className="text-xs font-semibold text-blue-600">{block.padY ?? 0}px</span>
        </div>
        <input
          type="range"
          min="0"
          max="60"
          step="2"
          value={block.padY ?? 0}
          onChange={(e) => set({ padY: Number(e.target.value) })}
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
        />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-slate-500">Lados</Label>
          <span className="text-xs font-semibold text-blue-600">{block.padX ?? 0}px</span>
        </div>
        <input
          type="range"
          min="0"
          max="60"
          step="2"
          value={block.padX ?? 0}
          onChange={(e) => set({ padX: Number(e.target.value) })}
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
        />
      </div>
    </div>

    {/* Vista previa en vivo */}
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-500">Vista previa</Label>
      <div className="flex items-center justify-center rounded-xl bg-slate-900 p-3">
        <span
          className="uppercase"
          style={{
            fontFamily: block.fontFamily || undefined,
            color: block.color || "#ffffff",
            fontSize: `${block.fontSize || defaultSize}px`,
            letterSpacing: `${block.letterSpacing ?? 0}em`,
            textAlign: block.align || "center",
            paddingTop: `${block.padY ?? 0}px`,
            paddingBottom: `${block.padY ?? 0}px`,
            paddingLeft: `${block.padX ?? 0}px`,
            paddingRight: `${block.padX ?? 0}px`,
            width: "100%",
            lineHeight: 1.15,
            fontWeight: 600,
            wordBreak: "break-word",
          }}
        >
          {block.text || "Vista previa del texto"}
        </span>
      </div>
    </div>
  </div>
);


const SOCIAL_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  whatsapp: "WhatsApp",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  x: "X (Twitter)",
  website: "Sitio web",
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-xs font-medium text-slate-500">{label}</Label>}
    {children}
  </div>
);

// Input con botón "✕" para borrar el contenido de un toque
const ClearInput = ({ value, onChange, wrapperClassName = "", className = "", ...props }) => (
  <div className={`relative ${wrapperClassName}`}>
    <Input
      value={value}
      onChange={onChange}
      className={`pr-9 ${className}`}
      {...props}
    />
    {value ? (
      <button
        type="button"
        aria-label="Borrar"
        data-testid="clear-input-btn"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onChange({ target: { value: "" } })}
        className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-slate-300 text-white transition hover:bg-slate-500"
      >
        <X className="h-3 w-3" strokeWidth={3} />
      </button>
    ) : null}
  </div>
);

// Botón universal para subir imagen/video: muestra vista previa del archivo
// y una barra verde de progreso (0-100%) mientras se sube.
const UploadImageBtn = ({ onUploaded, accept = "image/*", label = "Subir foto", maxMb = 70, className = "", currentUrl = "", currentType = "", purpose = "" }) => {
  const [busy, setBusy] = React.useState(false);
  const [stage, setStage] = React.useState("");
  const [pct, setPct] = React.useState(0);
  const [thumb, setThumb] = React.useState("");
  const [kind, setKind] = React.useState("image");

  const handle = async (file) => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) {
      alert(`El archivo supera el límite de ${maxMb} MB`);
      return;
    }
    const localUrl = URL.createObjectURL(file);
    setThumb(localUrl);
    setKind(file.type.startsWith("video") ? "video" : "image");
    setBusy(true);
    setPct(0);
    try {
      setStage("");
      const { url } = await apiUploadFile(file, setPct, { purpose, onStage: setStage });
      setPct(100);
      onUploaded(url, file);
    } catch (e) {
      alert("No se pudo subir el archivo. Intenta de nuevo.");
    } finally {
      setTimeout(() => {
        setBusy(false);
        setPct(0);
        setThumb("");
        URL.revokeObjectURL(localUrl);
      }, 500);
    }
  };

  if (busy) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border-2 border-blue-200 bg-blue-50/60 px-3 py-2"
        data-testid="upload-progress"
      >
        {kind === "video" ? (
          <video src={thumb} muted playsInline className="h-11 w-11 shrink-0 rounded-md object-cover" />
        ) : (
          <img src={thumb} alt="" className="h-11 w-11 shrink-0 rounded-md object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-2 text-[11px] font-medium">
            <span className="truncate text-slate-600">
              {stage === "converting"
                ? "Convirtiendo a música…"
                : `Subiendo ${purpose === "audio" ? "archivo" : kind === "video" ? "video" : "foto"}…`}
            </span>
            <span className="shrink-0 tabular-nums font-semibold text-green-600">{pct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-green-500 transition-all duration-200"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  const isVideoUrl =
    currentType === "video" ||
    (!currentType && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(currentUrl || ""));

  return (
    <div className="space-y-1.5">
      {currentUrl ? (
        <div className="relative overflow-hidden rounded-lg border border-slate-200">
          {isVideoUrl ? (
            <video
              src={/api\/files\//.test(currentUrl) ? `${currentUrl}#t=0.1` : currentUrl}
              muted
              playsInline
              preload="metadata"
              controls
              className="h-32 w-full bg-black object-contain"
            />
          ) : (
            <img src={currentUrl} alt="" className="h-32 w-full bg-black object-contain" />
          )}
          <span className="absolute left-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {isVideoUrl ? "✓ Video subido" : "✓ Foto subida"}
          </span>
        </div>
      ) : null}
      <label
        className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-blue-400 hover:text-blue-600 ${className}`}
      >
        <Upload className="h-3.5 w-3.5" /> {label}
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            handle(f);
          }}
        />
      </label>
    </div>
  );
};

// Selector de ícono del botón: iconos de marca + imágenes propias (guardadas en el navegador)
const CUSTOM_ICONS_KEY = "uaicard_custom_icons";
const readCustomIcons = () => {
  try {
    return JSON.parse(localStorage.getItem(CUSTOM_ICONS_KEY) || "[]");
  } catch {
    return [];
  }
};

const IconPicker = ({ block, set }) => {
  const [custom, setCustom] = React.useState(readCustomIcons);
  const [busy, setBusy] = React.useState(false);

  const persist = (list) => {
    setCustom(list);
    try {
      localStorage.setItem(CUSTOM_ICONS_KEY, JSON.stringify(list));
    } catch {}
  };

  const onUpload = async (file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;
    setBusy(true);
    try {
      const { url } = await apiUploadFile(file);
      persist([url, ...custom.filter((u) => u !== url)].slice(0, 40));
      set({ iconImage: url, icon: "" });
    } finally {
      setBusy(false);
    }
  };

  const removeCustom = (url) => {
    persist(custom.filter((u) => u !== url));
    if (block.iconImage === url) set({ iconImage: "" });
  };

  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Ícono</Label>
        <button
          type="button"
          onClick={() => set({ icon: "", iconImage: "" })}
          className={`text-xs underline ${
            block.icon || block.iconImage ? "text-slate-400" : "font-semibold text-blue-600"
          }`}
        >
          Automático
        </button>
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {BUTTON_ICONS.map(({ key, label, Icon, color, brand }) => {
          const active = block.icon === key && !block.iconImage;
          return (
            <button
              key={key}
              type="button"
              title={label}
              onClick={() =>
                set(
                  brand
                    ? { icon: key, iconImage: "", bgColor: color, opacity: 100 }
                    : { icon: key, iconImage: "" }
                )
              }
              className={`flex h-9 items-center justify-center rounded-lg border-2 transition ${
                active
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-slate-200 text-slate-600 hover:border-blue-300"
              }`}
            >
              <Icon className="h-4 w-4" style={{ color }} />
            </button>
          );
        })}
      </div>

      <div className="pt-1">
        <Label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Mis imágenes
        </Label>
        <div className="grid grid-cols-6 gap-1.5">
          {custom.map((url) => {
            const active = block.iconImage === url;
            return (
              <div key={url} className="group relative">
                <button
                  type="button"
                  data-testid="custom-icon-option"
                  onClick={() => set({ iconImage: url, icon: "" })}
                  className={`flex h-9 w-full items-center justify-center overflow-hidden rounded-lg border-2 transition ${
                    active ? "border-blue-600" : "border-slate-200 hover:border-blue-300"
                  }`}
                >
                  <img src={url} alt="" className="h-full w-full object-cover" />
                </button>
                <button
                  type="button"
                  onClick={() => removeCustom(url)}
                  className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white shadow group-hover:flex"
                  title="Eliminar"
                >
                  <X className="h-2.5 w-2.5" strokeWidth={3} />
                </button>
              </div>
            );
          })}
          <label
            data-testid="upload-icon-btn"
            className={`flex h-9 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed transition ${
              busy
                ? "border-blue-400 text-blue-500"
                : "border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600"
            }`}
            title="Subir imagen"
          >
            {busy ? <span className="text-[9px]">···</span> : <Upload className="h-4 w-4" />}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                onUpload(f);
              }}
            />
          </label>
        </div>
      </div>

      <p className="text-[11px] leading-snug text-slate-400">
        Elige una red social para el color oficial, o sube tu propia imagen como ícono. Tus imágenes se guardan aquí para reutilizarlas.
      </p>
    </div>
  );
};

const BlockEditor = ({ block, onChange }) => {
  const set = (patch) => onChange({ ...block, ...patch });

  const updateItem = (itemId, patch) =>
    set({
      items: block.items.map((it) =>
        it.id === itemId ? { ...it, ...patch } : it
      ),
    });

  const addItem = (template) =>
    set({ items: [...(block.items || []), { id: uid(), ...template }] });

  const removeItem = (itemId) =>
    set({ items: block.items.filter((it) => it.id !== itemId) });

  switch (block.type) {
    case "heading":
      return (
        <div className="space-y-3">
          <Field label="Título de la sección">
            <Input value={block.text} onChange={(e) => set({ text: e.target.value })} />
          </Field>
          <TextStyleControls block={block} set={set} defaultSize={14} />
        </div>
      );

    case "text":
      return (
        <div className="space-y-3">
          <Field label="Texto">
            <Textarea
              rows={3}
              value={block.text}
              onChange={(e) => set({ text: e.target.value })}
            />
          </Field>
          <TextStyleControls block={block} set={set} defaultSize={14} />
        </div>
      );

    case "divider": {
      const style = block.style || "gradient";
      return (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Estilo del divisor (animado)
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {DIVIDER_STYLES.map((opt) => {
                const active = style === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => set({ style: opt.key })}
                    className={`rounded-lg border-2 py-2 text-xs font-semibold transition ${
                      active
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-blue-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Color
            </Label>
            <input
              type="color"
              value={block.color || "#2E6BFF"}
              onChange={(e) => set({ color: e.target.value })}
              className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white"
            />
          </div>
        </div>
      );
    }

    case "button": {
      const selEntry = BUTTON_ICONS.find((i) => i.key === block.icon) || null;
      const PreviewIcon = selEntry?.Icon;
      const iconStyle = block.iconStyle || "solid";
      const iconColor = block.iconColor || "#ffffff";
      return (
        <div className="space-y-3">
          <Field label="Tipo de botón">
            <Select value={block.variant} onValueChange={(v) => set({ variant: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Correo electrónico</SelectItem>
                <SelectItem value="link">Enlace / sitio web</SelectItem>
                <SelectItem value="pdf">Archivo PDF</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Etiqueta del botón">
            <Input value={block.label} onChange={(e) => set({ label: e.target.value })} />
          </Field>
          {block.variant === "pdf" ? (
            <PdfUploadField value={block.value} onChange={(v) => set({ value: v })} />
          ) : (
          <Field
            label={
              block.variant === "whatsapp"
                ? "Número (con lada, sin +)"
                : block.variant === "email"
                ? "Correo electrónico"
                : "URL"
            }
          >
            <ClearInput
              value={block.value}
              inputMode={block.variant === "whatsapp" ? "numeric" : undefined}
              onChange={(e) => set({ value: e.target.value })}
            />
          </Field>
          )}
          {block.variant === "whatsapp" && (
            <Field label="Mensaje predeterminado">
              <Textarea
                rows={2}
                value={block.message || ""}
                onChange={(e) => set({ message: e.target.value })}
              />
            </Field>
          )}

          {/* Ícono del botón */}
          <IconPicker block={block} set={set} />

          {/* Estilo del icono: relleno vs transparente (recoloreable) */}
          <div className="space-y-2 rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Estilo del icono
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "solid", label: "Relleno" },
                { key: "outline", label: "Transparente" },
              ].map((opt) => {
                const active = iconStyle === opt.key;
                const preview =
                  opt.key === "solid" ? (
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full"
                      style={{ background: selEntry?.color || "#2E6BFF" }}
                    >
                      {PreviewIcon ? <PreviewIcon className="h-4 w-4 text-white" /> : null}
                    </span>
                  ) : (
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full border-2"
                      style={{ borderColor: iconColor, color: iconColor }}
                    >
                      {PreviewIcon ? <PreviewIcon className="h-4 w-4" /> : null}
                    </span>
                  );
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => set({ iconStyle: opt.key })}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border-2 py-2 text-xs font-semibold transition ${
                      active
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-blue-300"
                    }`}
                  >
                    {preview}
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {iconStyle === "outline" && (
              <div className="flex items-center gap-3 pt-1">
                <Label className="text-xs font-medium text-slate-500">Color del icono</Label>
                <input
                  type="color"
                  value={iconColor}
                  onChange={(e) => set({ iconColor: e.target.value })}
                  className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white"
                />
              </div>
            )}
          </div>

          {/* Diseño del botón (15 estilos con movimiento) */}
          <div className="space-y-2 rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Diseño del botón
            </Label>
            <div className="rounded-xl bg-slate-800 p-2">
              <div className="grid grid-cols-3 gap-2">
                {BUTTON_SHAPES.map((sh) => {
                  const active = (block.shape || "rounded") === sh.key;
                  return (
                    <button
                      key={sh.key}
                      type="button"
                      onClick={() => set({ shape: sh.key })}
                      title={sh.label}
                      className={`rounded-lg p-1.5 transition ${
                        active ? "bg-blue-600/30 ring-2 ring-blue-400" : "hover:bg-white/10"
                      }`}
                    >
                      <div
                        className={`blb-mini bsh bsh-${sh.key}`}
                        style={{ "--blb-border": block.borderColor || "rgba(255,255,255,0.7)" }}
                      >
                        <span className="mdot" />
                        <span className="mbar" />
                      </div>
                      <span className="mt-1 block truncate text-[9px] text-white/70">
                        {sh.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-xs font-medium text-slate-500">Color del contorno</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => set({ borderColor: "" })}
                  className="text-[11px] text-slate-400 underline"
                >
                  Auto
                </button>
                <input
                  type="color"
                  value={block.borderColor || "#ffffff"}
                  onChange={(e) => set({ borderColor: e.target.value })}
                  className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white"
                />
              </div>
            </div>
          </div>

          {/* Color y transparencia */}
          <div className="space-y-3 rounded-xl bg-slate-50 p-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Relleno del botón
              </Label>
              <button
                type="button"
                onClick={() => set({ bgColor: "", fillType: "solid" })}
                className="text-xs text-slate-400 underline"
              >
                Predeterminado
              </button>
            </div>
            {/* Tipo de relleno: sólido vs degradado */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "solid", label: "Color sólido" },
                { key: "gradient", label: "Degradado" },
              ].map((opt) => {
                const active = (block.fillType || "solid") === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => set({ fillType: opt.key })}
                    className={`rounded-lg border-2 py-2 text-xs font-semibold transition ${
                      active
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-blue-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {block.fillType === "gradient" ? (
              <div className="space-y-2.5">
                <div
                  className="h-10 w-full rounded-lg border border-slate-200"
                  style={{
                    background: `linear-gradient(${
                      typeof block.gradAngle === "number" ? block.gradAngle : 90
                    }deg, ${block.gradColor1 || "#2E6BFF"}, ${block.gradColor2 || "#8B5CF6"})`,
                  }}
                />
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Color 1</Label>
                    <input
                      type="color"
                      value={block.gradColor1 || "#2E6BFF"}
                      onChange={(e) => set({ gradColor1: e.target.value })}
                      className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Color 2</Label>
                    <input
                      type="color"
                      value={block.gradColor2 || "#8B5CF6"}
                      onChange={(e) => set({ gradColor2: e.target.value })}
                      className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-slate-500">Dirección</Label>
                    <span className="text-xs font-semibold text-blue-600">
                      {typeof block.gradAngle === "number" ? block.gradAngle : 90}°
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="15"
                    value={typeof block.gradAngle === "number" ? block.gradAngle : 90}
                    onChange={(e) => set({ gradAngle: Number(e.target.value) })}
                    className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={block.bgColor || "#2E6BFF"}
                  onChange={(e) => set({ bgColor: e.target.value })}
                  className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white"
                />
                <span className="text-xs text-slate-500">
                  {block.bgColor
                    ? block.bgColor.toUpperCase()
                    : "Estilo vidrio (por defecto)"}
                </span>
              </div>
            )}

            {(block.bgColor || block.fillType === "gradient") && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-500">
                    Transparencia
                  </Label>
                  <span className="text-xs font-semibold text-blue-600">
                    {typeof block.opacity === "number" ? block.opacity : 100}%
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={typeof block.opacity === "number" ? block.opacity : 100}
                  onChange={(e) => set({ opacity: Number(e.target.value) })}
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                />
              </div>
            )}
          </div>

          {/* Color del texto */}
          <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Color del texto
            </Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={block.textColor || "#ffffff"}
                onChange={(e) => set({ textColor: e.target.value })}
                className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white"
              />
              <button
                type="button"
                onClick={() => set({ textColor: "" })}
                className="text-xs text-slate-400 underline"
              >
                Blanco
              </button>
            </div>
          </div>

          {/* Animación */}
          <Field label="Animación del botón">
            <Select value={block.anim || "none"} onValueChange={(v) => set({ anim: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUTTON_ANIMS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      );
    }

    case "socials": {
      const style = block.style || "original";
      const iconColor = block.iconColor || "#ffffff";
      const size = block.iconSize || 48;
      return (
        <div className="space-y-3">
          {/* Estilo de los iconos */}
          <div className="space-y-2 rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Estilo de los logos
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "original", label: "Logos originales" },
                { key: "transparent", label: "Transparente" },
              ].map((opt) => {
                const active = style === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => set({ style: opt.key })}
                    className={`rounded-lg border-2 py-2 text-xs font-semibold transition ${
                      active
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-blue-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {style === "transparent" && (
              <div className="flex items-center justify-between pt-1">
                <Label className="text-xs font-medium text-slate-500">Color del icono</Label>
                <input
                  type="color"
                  value={iconColor}
                  onChange={(e) => set({ iconColor: e.target.value })}
                  className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white"
                />
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <Label className="text-xs font-medium text-slate-500">
                Tamaño ({size}px)
              </Label>
              <input
                type="range"
                min={36}
                max={72}
                value={size}
                onChange={(e) => set({ iconSize: Number(e.target.value) })}
                className="w-32"
              />
            </div>
          </div>

          {(block.items || []).map((s) => (
            <div key={s.id} className="flex items-center gap-2">
              <Select value={s.platform} onValueChange={(v) => updateItem(s.id, { platform: v })}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOCIAL_PLATFORMS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {SOCIAL_LABELS[p] || p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <ClearInput
                wrapperClassName="flex-1"
                value={s.url}
                placeholder="https://..."
                onChange={(e) => updateItem(s.id, { url: e.target.value })}
              />
              <button
                onClick={() => removeItem(s.id)}
                className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => addItem({ platform: "instagram", url: "https://" })}
          >
            <Plus className="h-4 w-4" /> Agregar red social
          </Button>
        </div>
      );
    }

    case "location":
      return (
        <div className="space-y-3">
          <Field label="Imagen de fondo">
            <Input value={block.image} placeholder="Pega una URL o sube una foto abajo" onChange={(e) => set({ image: e.target.value })} />
          </Field>
          <UploadImageBtn
            accept="image/*"
            maxMb={200}
            currentUrl={block.image || ""}
            label={block.image ? "Cambiar foto" : "Subir foto (máx. 200 MB)"}
            onUploaded={(url) => set({ image: url })}
          />
          {block.image && (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <img src={block.image} alt="preview" className="h-32 w-full object-cover" />
            </div>
          )}
          <Field label="Texto del botón">
            <Input value={block.label} onChange={(e) => set({ label: e.target.value })} />
          </Field>
          <Field label="Enlace de mapa (Google Maps)">
            <ClearInput value={block.mapUrl} onChange={(e) => set({ mapUrl: e.target.value })} />
          </Field>
          <Field label="Dirección o coordenadas (para Waze)">
            <Input
              value={block.address || ""}
              placeholder="Ej: Av. Reforma 100, CDMX  ·  o  19.4326,-99.1332"
              onChange={(e) => set({ address: e.target.value })}
            />
          </Field>
          <p className="text-[11px] text-slate-400">
            Se muestran 2 botones: Maps (usa el enlace) y Waze (usa la dirección/coordenadas; si están vacías, intenta leerlas del enlace de Maps).
          </p>
        </div>
      );

    case "products":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <TitleSize block={block} set={set} />
          <Field label="Subtítulo">
            <Input value={block.subtitle} onChange={(e) => set({ subtitle: e.target.value })} />
          </Field>
          <SubtitleSize block={block} set={set} />
          <div className="space-y-3 rounded-xl bg-slate-50 p-3">
            {(block.items || []).map((it, i) => (
              <div key={it.id} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500">
                    Producto {i + 1}
                  </span>
                  <button
                    onClick={() => removeItem(it.id)}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Input
                  placeholder="Título"
                  value={it.title}
                  onChange={(e) => updateItem(it.id, { title: e.target.value })}
                />
                {it.image && (
                  <img src={it.image} alt="" className="h-24 w-full rounded-md object-cover" />
                )}
                <ClearInput
                  placeholder="Imagen (URL)"
                  value={it.image}
                  onChange={(e) => updateItem(it.id, { image: e.target.value })}
                />
                <UploadImageBtn
                  label={it.image ? "Cambiar foto" : "Subir foto"}
                  onUploaded={(url) => updateItem(it.id, { image: url })}
                />
                <ClearInput
                  placeholder="Enlace (URL)"
                  value={it.link}
                  onChange={(e) => updateItem(it.id, { link: e.target.value })}
                />
                <PayLinkField InputCmp={ClearInput} value={it.payLink} onChange={(v) => updateItem(it.id, { payLink: v })} />
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={() =>
                addItem({ image: "", title: "Nuevo producto", link: "https://" })
              }
            >
              <Plus className="h-4 w-4" /> Agregar producto
            </Button>
          </div>
        </div>
      );

    case "showcase":
    case "showcase_strip": {
      const count = (block.items || []).length;
      const MAX = 20;
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <TitleSize block={block} set={set} />
          <Field label="Subtítulo (opcional)">
            <Input value={block.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} />
          </Field>
          <SubtitleSize block={block} set={set} />
          {block.type === "showcase_strip" && (
            <div className="space-y-2 rounded-xl bg-slate-50 p-3">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Movimiento de las tarjetas
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "loop", label: "Bucle ←" },
                  { key: "lateral", label: "Lateral ↔" },
                  { key: "fixed", label: "Fijo" },
                ].map((opt) => {
                  const active = (block.stripMotion || "loop") === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => set({ stripMotion: opt.key })}
                      className={`rounded-lg border-2 py-2 text-xs font-semibold transition ${
                        active
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-slate-200 text-slate-600 hover:border-blue-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400">
                Bucle: se deslizan solas lento hacia la izquierda. Lateral: vaivén. Fijo: solo al deslizar con el dedo.
              </p>
            </div>
          )}
          <p className="text-xs text-slate-400">
            {count}/{MAX} productos o servicios.
          </p>
          <div className="space-y-3 rounded-xl bg-slate-50 p-3">
            {(block.items || []).map((it, i) => {
              const mediaType = it.mediaType || "image";
              const btnVariant = it.btnVariant || "whatsapp";
              return (
                <div key={it.id} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">
                      {i + 1}. Producto / servicio
                    </span>
                    <button
                      onClick={() => removeItem(it.id)}
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Media type selector */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "image", label: "Imagen" },
                      { key: "video", label: "Video" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => updateItem(it.id, { mediaType: opt.key })}
                        className={`rounded-lg border-2 py-1.5 text-xs font-semibold transition ${
                          mediaType === opt.key
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-slate-200 text-slate-600 hover:border-blue-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Preview */}
                  {mediaType === "video" && it.video ? (
                    <video src={it.video} className="h-24 w-full rounded-md object-cover" muted loop autoPlay playsInline />
                  ) : it.image ? (
                    <img src={it.image} alt="" className="h-24 w-full rounded-md object-cover" />
                  ) : null}

                  {mediaType === "video" ? (
                    <>
                      <ClearInput
                        placeholder="URL del video (mp4/webm)"
                        value={it.video || ""}
                        onChange={(e) => updateItem(it.id, { video: e.target.value })}
                      />
                      <UploadImageBtn
                        accept="video/*"
                        maxMb={70}
                        currentUrl={it.video || ""}
                        currentType="video"
                        label="Subir video (máx. 70 MB)"
                        onUploaded={(url) => updateItem(it.id, { video: url, mediaType: "video" })}
                      />
                    </>
                  ) : (
                    <>
                      <ClearInput
                        placeholder="Imagen (URL)"
                        value={it.image || ""}
                        onChange={(e) => updateItem(it.id, { image: e.target.value })}
                      />
                      <UploadImageBtn
                        accept="image/*"
                        maxMb={70}
                        currentUrl={it.image || ""}
                        label="Subir imagen (máx. 70 MB)"
                        onUploaded={(url) => updateItem(it.id, { image: url, mediaType: "image" })}
                      />
                    </>
                  )}

                  <Input
                    placeholder="Nombre del producto / servicio"
                    value={it.title || ""}
                    onChange={(e) => updateItem(it.id, { title: e.target.value })}
                  />
                  <Textarea
                    rows={2}
                    placeholder="Descripción (opcional)"
                    value={it.description || ""}
                    onChange={(e) => updateItem(it.id, { description: e.target.value })}
                  />

                  <PayLinkField InputCmp={ClearInput} value={it.payLink} onChange={(v) => updateItem(it.id, { payLink: v })} />

                  {/* Schedule button */}
                  <div className="rounded-lg bg-slate-50 p-2 space-y-2">
                    <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Botón para agendar
                    </Label>
                    <Select value={btnVariant} onValueChange={(v) => updateItem(it.id, { btnVariant: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="link">Enlace / URL</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Texto del botón (ej. Agendar cita)"
                      value={it.btnLabel || ""}
                      onChange={(e) => updateItem(it.id, { btnLabel: e.target.value })}
                    />
                    <Input
                      placeholder={btnVariant === "whatsapp" ? "Número (con lada, sin +)" : "URL (https://...)"}
                      value={it.btnValue || ""}
                      onChange={(e) => updateItem(it.id, { btnValue: e.target.value })}
                    />
                    {btnVariant === "whatsapp" && (
                      <Textarea
                        rows={2}
                        placeholder="Mensaje predeterminado (opcional)"
                        value={it.btnMessage || ""}
                        onChange={(e) => updateItem(it.id, { btnMessage: e.target.value })}
                      />
                    )}
                  </div>
                </div>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              disabled={count >= MAX}
              onClick={() =>
                addItem({
                  mediaType: "image",
                  image: "",
                  video: "",
                  title: "Nuevo producto",
                  description: "",
                  btnVariant: "whatsapp",
                  btnLabel: "Agendar",
                  btnValue: "",
                  btnMessage: "",
                })
              }
            >
              <Plus className="h-4 w-4" />
              {count >= MAX ? `Máximo ${MAX}` : "Agregar producto / servicio"}
            </Button>
          </div>
        </div>
      );
    }

    case "gallery":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <TitleSize block={block} set={set} />
          <div className="space-y-3 rounded-xl bg-slate-50 p-3">
            {(block.items || []).map((it, i) => (
              <div key={it.id} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500">
                    Imagen {i + 1}
                  </span>
                  <button
                    onClick={() => removeItem(it.id)}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {it.image && (
                  <img src={it.image} alt="" className="h-24 w-full rounded-md object-cover" />
                )}
                <ClearInput
                  placeholder="Imagen (URL)"
                  value={it.image}
                  onChange={(e) => updateItem(it.id, { image: e.target.value })}
                />
                <UploadImageBtn
                  label={it.image ? "Cambiar foto" : "Subir foto"}
                  onUploaded={(url) => updateItem(it.id, { image: url })}
                />
                <Input
                  placeholder="Texto"
                  value={it.caption}
                  onChange={(e) => updateItem(it.id, { caption: e.target.value })}
                />
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={() => addItem({ image: "", caption: "Foto" })}
            >
              <Plus className="h-4 w-4" /> Agregar imagen
            </Button>
          </div>
        </div>
      );

    case "mosaic":
    case "flyers":
    case "mosaic_h": {
      const count = (block.items || []).length;
      const isFly = block.type === "flyers";
      const isH = block.type === "mosaic_h";
      const max = isFly ? 8 : isH ? 10 : 12;
      const mediaKind = (file) => (file?.type?.startsWith("video") ? "video" : "image");
      const isVid = (it) => it.kind === "video" || /\.(mp4|webm|mov)(\?|#|$)/i.test(it.image || "");
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <TitleSize block={block} set={set} />
          {isH && (
            <Field label="Velocidad de la tira">
              <div className="grid grid-cols-3 gap-1.5" data-testid="mosaic-speed">
                {[["slow", "Lento"], ["normal", "Normal"], ["fast", "Rápido"]].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set({ speed: v })}
                    className={`rounded-lg border px-2 py-2 text-xs font-semibold transition ${
                      (block.speed || "normal") === v
                        ? "border-blue-500 bg-blue-50 text-blue-600"
                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    data-testid={`mosaic-speed-${v}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </Field>
          )}
          {isFly && (
            <div className="space-y-2 rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-semibold text-slate-500">Formato del carrusel</p>
              <div className="grid grid-cols-2 gap-1.5" data-testid="flyers-orientation">
                {[["vertical", "360 vertical"], ["horizontal", "360 horizontal"]].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set({ orientation: v })}
                    className={`rounded-lg border px-2 py-2 text-xs font-semibold transition ${
                      (block.orientation || "vertical") === v ? "border-blue-500 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-500"
                    }`}
                    data-testid={`flyers-orientation-${v}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-1 text-xs font-semibold text-slate-500">
                <span>Tamaño del nombre del producto</span>
                <span className="text-blue-600">{block.nameSize || 18}px</span>
              </div>
              <input
                type="range"
                min={12}
                max={36}
                value={block.nameSize || 18}
                onChange={(e) => set({ nameSize: Number(e.target.value) })}
                className="w-full accent-blue-600"
                data-testid="flyers-name-size"
              />
            </div>
          )}
          <p className="text-xs text-slate-400">
            {isFly ? "Carrusel 3D giratorio de flayers (vertical 4:5 recomendado)." : isH ? "Tira horizontal continua (fotos verticales 9:16 recomendadas)." : "Mosaico minimalista (derecha a izquierda)."} {count}/{max} {isFly ? "flayers" : isH ? "fotos o videos" : "fotos"}.
          </p>
          <div className="space-y-3 rounded-xl bg-slate-50 p-3">
            {(block.items || []).map((it, i) => (
              <div key={it.id} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500">{isFly ? "Flayer" : isH && isVid(it) ? "Video" : "Foto"} {i + 1}</span>
                  <button
                    onClick={() => removeItem(it.id)}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {it.image &&
                  (isVid(it) ? (
                    <video src={it.image} muted playsInline className="h-20 w-full rounded-md bg-black object-cover" />
                  ) : (
                    <img src={it.image} alt="" className="h-20 w-full rounded-md object-cover" />
                  ))}
                <ClearInput
                  placeholder={isH ? "Imagen o video (URL)" : "Imagen (URL)"}
                  value={it.image}
                  onChange={(e) => updateItem(it.id, { image: e.target.value, kind: undefined })}
                />
                <UploadImageBtn
                  accept={isH ? "image/*,video/*" : "image/*"}
                  maxMb={isH ? 200 : 70}
                  label={isH ? "Subir foto o video" : "Subir foto"}
                  onUploaded={(url, file) => updateItem(it.id, { image: url, kind: mediaKind(file) })}
                />
                {isFly && (
                  <ClearInput
                    placeholder="Nombre del producto (se muestra debajo de la imagen)"
                    value={it.name || ""}
                    onChange={(e) => updateItem(it.id, { name: e.target.value })}
                    data-testid={`flyer-name-${i}`}
                  />
                )}
                {isFly && (
                  <div className="space-y-2 rounded-md bg-emerald-50/60 p-2">
                    <ClearInput
                      placeholder="WhatsApp (ej. 5215512345678)"
                      inputMode="tel"
                      value={it.wa || ""}
                      onChange={(e) => updateItem(it.id, { wa: e.target.value.replace(/[^\d+]/g, "") })}
                      data-testid={`flyer-wa-${i}`}
                    />
                    <ClearInput
                      placeholder="Mensaje (ej. Hola, quiero info de esta promoción)"
                      value={it.waMsg || ""}
                      onChange={(e) => updateItem(it.id, { waMsg: e.target.value })}
                    />
                    <p className="text-[11px] text-slate-400">Si lo dejas vacío, este flayer no muestra botón.</p>
                  </div>
                )}
              </div>
            ))}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={count >= max}
                onClick={() => addItem({ image: "" })}
              >
                <Plus className="h-4 w-4" />
                {count >= max ? `Máx. ${max}` : "Agregar (URL)"}
              </Button>
              {count >= max ? (
                <div className="pointer-events-none flex items-center justify-center rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-300">
                  Máx. {max}
                </div>
              ) : (
                <UploadImageBtn
                  accept={isH ? "image/*,video/*" : "image/*"}
                  maxMb={isH ? 200 : 70}
                  label={isH ? "Subir foto o video" : "Subir foto"}
                  onUploaded={(url, file) => addItem({ image: url, kind: mediaKind(file) })}
                />
              )}
            </div>
          </div>
        </div>
      );
    }

    case "youtube":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <TitleSize block={block} set={set} />
          <Field label="Enlace del video (YouTube, TikTok, Vimeo, Instagram, Facebook o MP4)">
            <ClearInput
              value={block.url}
              placeholder="Pega aquí el enlace..."
              onChange={(e) => set({ url: e.target.value, uploadUrl: "" })}
            />
          </Field>
          <div className="space-y-2 rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              O sube tu propio video (horizontal o vertical)
            </Label>
            {block.uploadUrl ? (
              <div className="space-y-2">
                <video src={block.uploadUrl} controls playsInline className="max-h-48 w-full rounded-lg bg-black object-contain" />
                <button
                  onClick={() => set({ uploadUrl: "" })}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Quitar video subido
                </button>
              </div>
            ) : (
              <UploadImageBtn
                accept="video/*"
                maxMb={800}
                label="Subir video (hasta 800 MB)"
                onUploaded={(url) => set({ uploadUrl: url, url: "" })}
              />
            )}
            <p className="text-[11px] leading-snug text-slate-400">
              Los videos grandes pueden tardar según tu conexión. Se optimizan automáticamente para reproducción rápida.
            </p>
          </div>
        </div>
      );

    case "vcard":
      return (
        <div className="space-y-3">
          <Field label="Texto del botón">
            <Input value={block.label || ""} onChange={(e) => set({ label: e.target.value })} />
          </Field>
          <Field label="Nombre completo / Negocio">
            <Input value={block.name || ""} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Empresa (opcional)">
            <Input value={block.org || ""} onChange={(e) => set({ org: e.target.value })} />
          </Field>
          <Field label="Puesto / Cargo (opcional)">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field label="Teléfono">
            <ClearInput value={block.phone || ""} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label="WhatsApp (con lada, sin +)">
            <ClearInput value={block.whatsapp || ""} inputMode="numeric" placeholder="521..." onChange={(e) => set({ whatsapp: e.target.value })} />
          </Field>
          <Field label="Correo">
            <ClearInput value={block.email || ""} onChange={(e) => set({ email: e.target.value })} />
          </Field>
          <Field label="Sitio web (opcional)">
            <ClearInput value={block.website || ""} onChange={(e) => set({ website: e.target.value })} />
          </Field>
          <div className="space-y-2 rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Página / Redes (opcional)</Label>
            {(block.links || []).map((lk) => (
              <div key={lk.id} className="flex items-center gap-2">
                <ClearInput
                  wrapperClassName="flex-1"
                  value={lk.url || ""}
                  placeholder="https://... (Facebook, Instagram, web...)"
                  onChange={(e) =>
                    set({ links: (block.links || []).map((l) => (l.id === lk.id ? { ...l, url: e.target.value } : l)) })
                  }
                />
                <button
                  onClick={() => set({ links: (block.links || []).filter((l) => l.id !== lk.id) })}
                  className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => set({ links: [...(block.links || []), { id: uid(), url: "https://" }] })}
            >
              <Plus className="h-4 w-4" /> Agregar enlace
            </Button>
          </div>
          <Field label="Dirección (opcional)">
            <ClearInput value={block.address || ""} onChange={(e) => set({ address: e.target.value })} />
          </Field>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Color del botón</Label>
            <input type="color" value={block.color || "#2E6BFF"} onChange={(e) => set({ color: e.target.value })} className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white" />
          </div>
        </div>
      );

    case "reviews":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <label className="flex cursor-pointer items-center gap-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
            <input type="checkbox" checked={block.allowPublic !== false} onChange={(e) => set({ allowPublic: e.target.checked })} className="h-4 w-4" data-testid="reviews-allow-public" />
            Permitir que los clientes escriban reseñas desde el perfil
          </label>
          {(block.items || []).map((r) => (
            <div key={r.id} className="space-y-2 rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2">
                <Input placeholder="Nombre del cliente" value={r.name || ""} onChange={(e) => updateItem(r.id, { name: e.target.value })} />
                <button onClick={() => removeItem(r.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Estrellas</Label>
                <Select value={String(r.rating || 5)} onValueChange={(v) => updateItem(r.id, { rating: Number(v) })}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((n) => (<SelectItem key={n} value={String(n)}>{n} ★</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea rows={2} placeholder="Comentario" value={r.text || ""} onChange={(e) => updateItem(r.id, { text: e.target.value })} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => addItem({ name: "Cliente", rating: 5, text: "" })}><Plus className="h-4 w-4" /> Agregar reseña</Button>
        </div>
      );

    case "pay":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <TitleSize block={block} set={set} />
          <Field label="Descripción (opcional)">
            <Textarea rows={2} value={block.description || ""} onChange={(e) => set({ description: e.target.value })} />
          </Field>
          <Field label="Precio a mostrar (opcional)">
            <Input placeholder="ej. $499 MXN" value={block.price || ""} onChange={(e) => set({ price: e.target.value })} />
          </Field>
          <Field label="Texto del botón">
            <Input value={block.btnLabel || ""} onChange={(e) => set({ btnLabel: e.target.value })} />
          </Field>
          <PayLinkField InputCmp={ClearInput} value={block.payLink} onChange={(v) => set({ payLink: v })} />
          <p className="text-[11px] leading-snug text-slate-400">
            Crea tu link en stripe.com → Productos → “Crear enlace de pago”, cópialo y pégalo aquí.
          </p>
        </div>
      );

    case "menu":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field label="Símbolo de moneda">
            <Input className="w-24" value={block.currency || "$"} onChange={(e) => set({ currency: e.target.value })} />
          </Field>
          {(block.items || []).map((it) => (
            <div key={it.id} className="space-y-2 rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2">
                <Input placeholder="Nombre" value={it.name || ""} onChange={(e) => updateItem(it.id, { name: e.target.value })} />
                <button onClick={() => removeItem(it.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input placeholder="Descripción (opcional)" value={it.description || ""} onChange={(e) => updateItem(it.id, { description: e.target.value })} />
              <Input placeholder="Precio (ej. 120)" value={it.price || ""} onChange={(e) => updateItem(it.id, { price: e.target.value })} />
              <PayLinkField InputCmp={ClearInput} value={it.payLink} onChange={(v) => updateItem(it.id, { payLink: v })} />
              <UploadImageBtn
                accept="image/*"
                maxMb={70}
                currentUrl={it.image || ""}
                label={it.image ? "Cambiar foto" : "Subir foto (opcional)"}
                onUploaded={(url) => updateItem(it.id, { image: url })}
              />
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => addItem({ name: "Producto", description: "", price: "0", image: "" })}><Plus className="h-4 w-4" /> Agregar ítem</Button>
          <div className="space-y-2 rounded-xl bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <input type="checkbox" checked={!!block.showOrder} onChange={(e) => set({ showOrder: e.target.checked })} className="h-4 w-4" />
              Mostrar botón "Pedir por WhatsApp"
            </label>
            {block.showOrder && (
              <>
                <Input placeholder="Texto del botón" value={block.orderLabel || ""} onChange={(e) => set({ orderLabel: e.target.value })} />
                <Input placeholder="WhatsApp (con lada, sin +)" value={block.orderPhone || ""} onChange={(e) => set({ orderPhone: e.target.value })} />
                <Textarea rows={2} placeholder="Mensaje predeterminado" value={block.orderMessage || ""} onChange={(e) => set({ orderMessage: e.target.value })} />
              </>
            )}
          </div>
        </div>
      );

    case "countdown":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field label="Subtítulo">
            <Input value={block.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} />
          </Field>
          <Field label="Fecha y hora límite">
            <Input type="datetime-local" value={block.targetDate || ""} onChange={(e) => set({ targetDate: e.target.value })} />
          </Field>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Color de acento</Label>
            <input type="color" value={block.color || "#EF4444"} onChange={(e) => set({ color: e.target.value })} className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white" />
          </div>
          <Field label="Texto del botón (opcional)">
            <Input value={block.btnLabel || ""} onChange={(e) => set({ btnLabel: e.target.value })} />
          </Field>
          <Field label="Tipo de acción">
            <Select value={block.btnVariant || "whatsapp"} onValueChange={(v) => set({ btnVariant: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Correo</SelectItem>
                <SelectItem value="link">Enlace</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={block.btnVariant === "whatsapp" ? "Número (con lada, sin +)" : block.btnVariant === "email" ? "Correo" : "URL"}>
            <Input value={block.btnValue || ""} onChange={(e) => set({ btnValue: e.target.value })} />
          </Field>
          {block.btnVariant === "whatsapp" && (
            <Field label="Mensaje">
              <Textarea rows={2} value={block.btnMessage || ""} onChange={(e) => set({ btnMessage: e.target.value })} />
            </Field>
          )}
        </div>
      );

    case "timeline":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Color</Label>
            <input type="color" value={block.color || "#2E6BFF"} onChange={(e) => set({ color: e.target.value })} className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white" />
          </div>
          {(block.items || []).map((it) => (
            <div key={it.id} className="space-y-2 rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2">
                <Input placeholder="Año / etapa (ej. 2024)" value={it.year || ""} onChange={(e) => updateItem(it.id, { year: e.target.value })} />
                <button onClick={() => removeItem(it.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input placeholder="Título del hito" value={it.title || ""} onChange={(e) => updateItem(it.id, { title: e.target.value })} />
              <Textarea rows={2} placeholder="Descripción" value={it.text || ""} onChange={(e) => updateItem(it.id, { text: e.target.value })} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => addItem({ year: "", title: "Nuevo hito", text: "" })}><Plus className="h-4 w-4" /> Agregar hito</Button>
        </div>
      );

    case "audio":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field label="Subtítulo (opcional)">
            <Input value={block.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} />
          </Field>
          <Field label="URL del audio (mp3) o video (mp4)">
            <Input placeholder="https://..." value={block.url || ""} onChange={(e) => set({ url: e.target.value })} />
          </Field>
          <UploadImageBtn
            accept="audio/*,video/*"
            maxMb={1024}
            purpose="audio"
            label="Subir música o video (hasta 1 GB)"
            onUploaded={(url) => set({ url })}
          />
          <p className="text-[11px] text-slate-400">
            Si subes un video, se convierte automáticamente en música (solo el audio, ligero y rápido).
          </p>
          <Field label="Cómo se muestra">
            <div className="grid grid-cols-2 gap-1.5" data-testid="audio-mode">
              {[["float", "Icono flotante"], ["card", "Tarjeta en la página"]].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set({ mode: v })}
                  className={`rounded-lg border px-2 py-2 text-xs font-semibold transition ${
                    (block.mode === "card" ? "card" : "float") === v
                      ? "border-blue-500 bg-blue-50 text-blue-600"
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid={`audio-mode-${v}`}
                >
                  {l}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-400">
              Icono flotante: la música inicia sola y se pausa o reanuda tocando el icono del lado derecho.
            </p>
          </Field>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Color</Label>
            <input type="color" value={block.color || "#2E6BFF"} onChange={(e) => set({ color: e.target.value })} className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white" />
          </div>
        </div>
      );

    case "stories":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          {(block.items || []).map((it) => (
            <div key={it.id} className="space-y-2 rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2">
                <Input placeholder="Etiqueta (ej. Novedades)" value={it.label || ""} onChange={(e) => updateItem(it.id, { label: e.target.value })} />
                <button onClick={() => removeItem(it.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <UploadImageBtn
                accept="image/*,video/*"
                maxMb={512}
                currentUrl={it.media || ""}
                currentType={it.mediaType || ""}
                label={it.media ? "Cambiar imagen/video" : "Subir imagen o video (máx. 512 MB)"}
                onUploaded={(url, file) => {
                  const mt = file && file.type.startsWith("video") ? "video" : "image";
                  updateItem(it.id, { media: url, mediaType: mt, ...(mt === "image" ? { thumb: url } : {}) });
                }}
              />
              {it.mediaType === "video" && (
                <UploadImageBtn
                  accept="image/*"
                  maxMb={70}
                  currentUrl={it.thumb || ""}
                  label="Miniatura del video (opcional)"
                  onUploaded={(url) => updateItem(it.id, { thumb: url })}
                />
              )}
              <Input placeholder="Texto en la historia (opcional)" value={it.caption || ""} onChange={(e) => updateItem(it.id, { caption: e.target.value })} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => addItem({ mediaType: "image", media: "", thumb: "", label: "Historia", caption: "" })}><Plus className="h-4 w-4" /> Agregar historia</Button>
        </div>
      );

    case "image": {
      const width = typeof block.width === "number" ? block.width : 100;
      const ratio = block.ratio || "auto";
      const radius = typeof block.radius === "number" ? block.radius : 16;
      const canCrop = ratio !== "auto";
      return (
        <div className="space-y-3">
          <UploadImageBtn
            accept="image/*"
            maxMb={200}
            currentUrl={block.url || ""}
            label={block.url ? "Cambiar imagen" : "Insertar / Subir imagen (máx. 200 MB)"}
            className="px-4 py-4 text-sm"
            onUploaded={(url) => set({ url })}
          />

          {block.url && (
            <>
              {/* Vista previa */}
              <div className="rounded-xl bg-slate-900 p-3">
                <div
                  className="mx-auto overflow-hidden bg-black/30"
                  style={{
                    width: `${width}%`,
                    aspectRatio:
                      ratio === "auto"
                        ? undefined
                        : { square: "1 / 1", "4:3": "4 / 3", "16:9": "16 / 9", "3:4": "3 / 4" }[ratio],
                    borderRadius: radius,
                  }}
                >
                  <img
                    src={block.url}
                    alt="preview"
                    className={ratio === "auto" ? "w-full" : "h-full w-full object-cover"}
                    style={
                      ratio === "auto"
                        ? { display: "block", borderRadius: radius }
                        : {
                            objectPosition: `${block.posX ?? 50}% ${block.posY ?? 50}%`,
                            transform: `scale(${block.zoom ?? 1})`,
                            transformOrigin: `${block.posX ?? 50}% ${block.posY ?? 50}%`,
                          }
                    }
                  />
                </div>
              </div>

              {/* Tamaño */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-500">Tamaño (ancho)</Label>
                  <span className="text-xs font-semibold text-blue-600">{width}%</span>
                </div>
                <input
                  type="range" min="30" max="100" step="1"
                  value={width}
                  onChange={(e) => set({ width: Number(e.target.value) })}
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                />
              </div>

              {/* Proporción / recorte */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500">Recorte (proporción)</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "auto", label: "Original" },
                    { key: "square", label: "Cuadrada" },
                    { key: "4:3", label: "4:3" },
                    { key: "16:9", label: "16:9" },
                    { key: "3:4", label: "Vertical" },
                  ].map((opt) => {
                    const active = ratio === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => set({ ratio: opt.key })}
                        className={`rounded-lg border-2 py-2 text-xs font-semibold transition ${
                          active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Zoom y posición (solo si hay recorte) */}
              {canCrop && (
                <div className="space-y-3 rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Ajustar recorte
                    </Label>
                    <button type="button" onClick={() => set({ posX: 50, posY: 50, zoom: 1 })} className="text-xs text-blue-600 underline">
                      Centrar
                    </button>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Horizontal</Label>
                    <input type="range" min="0" max="100" value={block.posX ?? 50}
                      onChange={(e) => set({ posX: Number(e.target.value) })}
                      className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Vertical</Label>
                    <input type="range" min="0" max="100" value={block.posY ?? 50}
                      onChange={(e) => set({ posY: Number(e.target.value) })}
                      className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-500">Zoom</Label>
                      <span className="text-xs font-semibold text-blue-600">{(block.zoom ?? 1).toFixed(1)}x</span>
                    </div>
                    <input type="range" min="1" max="3" step="0.1" value={block.zoom ?? 1}
                      onChange={(e) => set({ zoom: Number(e.target.value) })}
                      className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600" />
                  </div>
                </div>
              )}

              {/* Alineación */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500">Alineación</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "left", Icon: AlignLeft, label: "Izq." },
                    { key: "center", Icon: AlignCenter, label: "Centro" },
                    { key: "right", Icon: AlignRight, label: "Der." },
                  ].map((opt) => {
                    const active = (block.align || "center") === opt.key;
                    const I = opt.Icon;
                    return (
                      <button key={opt.key} type="button" onClick={() => set({ align: opt.key })}
                        className={`flex items-center justify-center gap-1 rounded-lg border-2 py-2 text-xs font-semibold transition ${
                          active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-300"
                        }`}>
                        <I className="h-4 w-4" /> {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Redondeo de esquinas */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-500">Esquinas redondeadas</Label>
                  <span className="text-xs font-semibold text-blue-600">{radius}px</span>
                </div>
                <input type="range" min="0" max="40" step="2" value={radius}
                  onChange={(e) => set({ radius: Number(e.target.value) })}
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600" />
              </div>
            </>
          )}
        </div>
      );
    }

    case "qr":
      return (
        <div className="space-y-3">
          <Field label="Título">
            <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field label="URL o texto del QR (vacío = esta página)">
            <Input placeholder="https://..." value={block.url || ""} onChange={(e) => set({ url: e.target.value })} />
          </Field>
          <Field label="Leyenda (opcional)">
            <Input value={block.caption || ""} onChange={(e) => set({ caption: e.target.value })} />
          </Field>
          <div className="flex gap-3">
            <div className="flex flex-1 items-center justify-between rounded-xl bg-slate-50 p-3">
              <Label className="text-xs font-medium text-slate-500">Color</Label>
              <input type="color" value={block.fg || "#0A1633"} onChange={(e) => set({ fg: e.target.value })} className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white" />
            </div>
            <div className="flex flex-1 items-center justify-between rounded-xl bg-slate-50 p-3">
              <Label className="text-xs font-medium text-slate-500">Fondo</Label>
              <input type="color" value={block.bg || "#ffffff"} onChange={(e) => set({ bg: e.target.value })} className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white" />
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default BlockEditor;